import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Palette } from '../styles/Palette';
import { StyledButton, StyledButtonGroup } from '../styles/StyledButtons';
import { FundOpportunityLabels } from '../Util/Labels';
import { ALL_SECTOR_OPTIONS, FundOpportunitySector } from './fundOpportunitySectorOptions';

type FieldInputType = 'text' | 'number' | 'date' | 'sector' | 'none';

const FIELD_INPUT_TYPES: Record<string, FieldInputType> = {
  closeDate: 'date',
  fundSize: 'number',
  fundDuration: 'number',
  returnOnInvestment: 'number',
  interest: 'number',
  seniorDebtLvr: 'number',
  siteArea: 'number',
  currentValuation: 'number',
  sector: 'sector',
  addresses: 'none',
};

const inputTypeFor = (fieldName: string): FieldInputType => FIELD_INPUT_TYPES[fieldName] ?? 'text';

function MissingFieldEditor({
  inputType,
  draftValue,
  draftSector,
  onDraftValueChange,
  onToggleSector,
  onSave,
  onCancel,
}: {
  inputType: FieldInputType;
  draftValue: string | number;
  draftSector: FundOpportunitySector[];
  onDraftValueChange: (v: string) => void;
  onToggleSector: (v: FundOpportunitySector) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  if (inputType === 'sector') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 360 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {ALL_SECTOR_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={
                <Checkbox
                  size="small"
                  checked={draftSector.includes(opt.value)}
                  onChange={() => onToggleSector(opt.value)}
                />
              }
              label={opt.label}
            />
          ))}
        </Box>
        <StyledButtonGroup>
          <StyledButton onClick={onSave}>Save</StyledButton>
          <StyledButton onClick={onCancel}>Cancel</StyledButton>
        </StyledButtonGroup>
      </Box>
    );
  }
  if (inputType === 'none') {
    return (
      <Typography variant="body2">Provide at least one address using the chat above.</Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 360 }}>
      <TextField
        size="small"
        type={inputType === 'number' ? 'number' : inputType === 'date' ? 'date' : 'text'}
        value={draftValue}
        onChange={(e) => onDraftValueChange(e.target.value)}
        autoFocus
        InputLabelProps={inputType === 'date' ? { shrink: true } : undefined}
      />
      <StyledButtonGroup>
        <StyledButton onClick={onSave}>Save</StyledButton>
        <StyledButton onClick={onCancel}>Cancel</StyledButton>
      </StyledButtonGroup>
    </Box>
  );
}

interface MissingFundOpportunityFieldRowProps {
  fieldName: string;
  index: number;
  required?: boolean;
  onSetField: (fieldName: string, value: string | number | string[]) => void;
}

function MissingFieldValue({
  isEditing,
  required,
  inputType,
  draftValue,
  draftSector,
  onDraftValueChange,
  onToggleSector,
  onSave,
  onCancel,
}: {
  isEditing: boolean;
  required: boolean;
  inputType: FieldInputType;
  draftValue: string | number;
  draftSector: FundOpportunitySector[];
  onDraftValueChange: (v: string) => void;
  onToggleSector: (v: FundOpportunitySector) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  if (isEditing) {
    return (
      <MissingFieldEditor
        inputType={inputType}
        draftValue={draftValue}
        draftSector={draftSector}
        onDraftValueChange={onDraftValueChange}
        onToggleSector={onToggleSector}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }
  return (
    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
      {required ? 'Required — not yet provided' : 'Not yet provided'}
    </Typography>
  );
}

function valueToSave(
  inputType: FieldInputType,
  draftValue: string | number,
  draftSector: FundOpportunitySector[],
): string | number | string[] | null {
  if (inputType === 'sector') return draftSector.length === 0 ? null : draftSector;
  if (draftValue === '') return null;
  return inputType === 'number' ? Number(draftValue) : draftValue;
}

export const MissingFundOpportunityFieldRow: React.FC<MissingFundOpportunityFieldRowProps> = ({
  fieldName,
  index,
  required = true,
  onSetField,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftValue, setDraftValue] = useState<string | number>('');
  const [draftSector, setDraftSector] = useState<FundOpportunitySector[]>([]);
  const label = FundOpportunityLabels[fieldName] || fieldName;
  const inputType = inputTypeFor(fieldName);

  return (
    <TableRow
      sx={{
        backgroundColor: index % 2 === 0 ? Palette.tone1.background : 'white',
        '&:hover': { backgroundColor: Palette.tone1.activeBg },
      }}
    >
      <TableCell sx={{ width: 48 }} />
      <TableCell
        sx={{ fontWeight: 'bold', color: required ? Palette.danger.text : Palette.tone1.text }}
      >
        {label}
      </TableCell>
      <TableCell sx={{ color: required ? Palette.danger.text : Palette.tone1.text }}>
        <MissingFieldValue
          isEditing={isEditing}
          required={required}
          inputType={inputType}
          draftValue={draftValue}
          draftSector={draftSector}
          onDraftValueChange={setDraftValue}
          onToggleSector={(value) =>
            setDraftSector((prev) =>
              prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
            )
          }
          onSave={() => {
            const value = valueToSave(inputType, draftValue, draftSector);
            if (value === null) return;
            onSetField(fieldName, value);
            setIsEditing(false);
          }}
          onCancel={() => {
            setIsEditing(false);
            setDraftValue('');
            setDraftSector([]);
          }}
        />
      </TableCell>
      <TableCell align="right">
        {!isEditing && inputType !== 'none' && (
          <IconButton
            size="small"
            onClick={() => setIsEditing(true)}
            sx={{ color: Palette.tone1.text }}
            aria-label={`Fill in ${label}`}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};

export default MissingFundOpportunityFieldRow;
